import React, { useEffect, useState, useCallback } from 'react';
import { useSelector } from 'react-redux';
import Form from 'react-bootstrap/Form';
import { v4 as uuidv4 } from 'uuid';
import _ from 'lodash';
import { toast } from 'react-toastify';
import {
    fetchAllEmployeePermission,
    fetchRoleList,
    updatePermission
} from '../../store/actions/auth';
import { findPermissionByRole } from '../../config/utils';
import PropTypes from 'prop-types';

// ---------- Child Components ----------
const RoleCheckbox = ({ role, checked, onChange }) => (
    <div className={`col-${role.colSize || 2} pt-2 pb-2 alignCenter`}>
        <Form.Check
            type="checkbox"
            checked={checked}
            className="ml-0 moduleEnable"
            onChange={onChange}
        />
    </div>
);

RoleCheckbox.propTypes = {
    role: PropTypes.object.isRequired,
    checked: PropTypes.bool.isRequired,
    onChange: PropTypes.func.isRequired
}

const PermissionRow = ({ roles, permission, onPermissionChange, findPermission }) => (
    <div className="row permissionRow permissionEnable" key={uuidv4()}>
        <div className="col-3 pt-2 pb-2 pl-5">
            <span className="module ml-permission">{permission.permissionName}</span>
        </div>
        {roles.map(role => (
            <RoleCheckbox
                key={role.name}
                role={role}
                checked={findPermission(role.name, permission.roleDetails)}
                onChange={e =>
                    onPermissionChange(e, permission.featureId, permission.subFeatureId, permission.permissionId, role.name)
                }
            />
        ))}
    </div>
);

PermissionRow.propTypes = {
    roles: PropTypes.array.isRequired,
    permission: PropTypes.object.isRequired,
    onPermissionChange: PropTypes.func.isRequired,
    findPermission: PropTypes.func.isRequired
}

const SubFeatureRow = ({
    subfeature,
    featureId,
    roles,
    onPermissionChange,
    findPermission,
    onExpand,
    index
}) => (
    <div key={uuidv4()}>
        <div className="row permissionRow featureEnable">
            
            <button
                className="col-3 pt-2 pb-2 pl-5 expandSubFeature cursorPointer sortBtn permissionEnable"
                onClick={() => onExpand(index)}
            >
                <span className="module">
                    <i
                        className={`fa ${subfeature.showSubPermission ? 'fa-angle-down' : 'fa-angle-right'
                            } mr-2`}
                        aria-hidden="true"
                    ></i>
                    {subfeature.subFeatureName}
                </span>
            </button>
            {roles.map(role => (
                <RoleCheckbox
                    key={role.name}
                    role={role}
                    checked={findPermission(role.name, subfeature.roleDetails)}
                    onChange={e =>
                        onPermissionChange(e, featureId, subfeature.subFeatureId, null, role.name)
                    }
                />
            ))}
        </div>

        {subfeature.showSubPermission &&
            subfeature.Permissions?.map(permission => (
                <PermissionRow
                    key={uuidv4()}
                    roles={roles}
                    permission={permission}
                    onPermissionChange={onPermissionChange}
                    findPermission={findPermission}
                />
            ))}
    </div>
);

SubFeatureRow.propTypes = {
    subfeature: PropTypes.object.isRequired,
    featureId: PropTypes.number.isRequired,
    roles: PropTypes.array.isRequired,
    onPermissionChange: PropTypes.func.isRequired,
    findPermission: PropTypes.func.isRequired,
    onExpand: PropTypes.func.isRequired,
    index: PropTypes.number.isRequired
}

const FeatureRow = ({
    permission,
    index,
    roles,
    onExpand,
    findPermission,
    onPermissionChange
}) => (
    <div
        className={`${permission.showSubPermission ? 'expanded' : ''} row permissionRow`}
        key={permission.id}
    >
        <button
            className="col-3 pt-2 pb-2 moduleBar cursorPointer sortBtn permissionEnable"
            onClick={() => onExpand(index)}
        >
            <span className="module">
                <i
                    className={`fa ${permission.showSubPermission ? 'fa-angle-down' : 'fa-angle-right'
                        } mr-2`}
                    aria-hidden="true"
                ></i>
                {permission.featureName}
            </span>
        </button>
        {roles.map(role => (
            <RoleCheckbox
                key={role.name}
                role={role}
                checked={findPermission(role.name, permission.roleDetails)}
                onChange={e =>
                    onPermissionChange(e, permission.featureId, null, null, role.name)
                }
            />
        ))}

        {permission.showSubPermission && (
            <div className="col-12">
                {permission.SubFeatures?.map((subfeature, subIdx) => (
                    <SubFeatureRow
                        key={uuidv4()}
                        subfeature={subfeature}
                        featureId={permission.featureId}
                        roles={roles}
                        onPermissionChange={onPermissionChange}
                        findPermission={findPermission}
                        onExpand={expandedIdx => onExpand(index, expandedIdx)}
                        index={subIdx}
                    />
                ))}
            </div>
        )}
    </div>
);

FeatureRow.propTypes = {
    permission: PropTypes.object.isRequired,
    index: PropTypes.number.isRequired,
    roles: PropTypes.array.isRequired,
    onExpand: PropTypes.func.isRequired,
    findPermission: PropTypes.func.isRequired,
    onPermissionChange: PropTypes.func.isRequired
}

// ---------- Main Component ----------
const PermissionTable = ({ dispatch }) => {
    const { roleList, allEmployeePermission } = useSelector(state => state?.auth);
    const [permissionList, setPermissionList] = useState([]);
    const [roles, setRoleList] = useState([]);

    useEffect(() => {
        if (!roleList.length) dispatch(fetchRoleList());
    }, []);

    useEffect(() => {
        if (roleList.length) {
            let rolesData = roleList.map(role => {
                if (role.name === 'Admin') {
                    return { name: role.name, colSize: 1 }
                } else if (role.name === 'Seller') {
                    return { name: role.name, colSize: 1 }
                } else if (role.name === 'Super Admin') {
                    return { name: role.name, colSize: 1 }
                } else if (role.name === 'Warehouse Manager') {
                    return { name: role.name, colSize: 2 }
                } else if (role.name === 'Warehouse Worker') {
                    return { name: role.name, colSize: 2 }
                } else if (role.name === 'Onboarding Seller') {
                    return { name: role.name, colSize: 2 }
                } else {
                    return { name: role.name, colSize: 2 }
                }
            });
            setRoleList(rolesData);
        }
    }, [roleList]);

    useEffect(() => {
        if (!allEmployeePermission.length) {
            dispatch(fetchAllEmployeePermission());
        } else {
            handleMergePermissions();
        }
    }, [allEmployeePermission]);

    const handleExpand = useCallback((index, subIndex = null) => {
        setPermissionList(prevList => {
            const updated = _.cloneDeep(prevList);
            if (subIndex !== null) {
                updated[index].SubFeatures[subIndex].showSubPermission =
                    !updated[index].SubFeatures[subIndex].showSubPermission;
            } else {
                updated[index].showSubPermission = !updated[index].showSubPermission;
            }
            return updated;
        });
    }, []);

    const findPermission = useCallback(
        (role, permissions) => {
            if (roleList.length) {
                return findPermissionByRole(role, permissions, roleList);
            }
            return false;
        },
        [roleList]
    );

    const handlePermissionChange = useCallback(
        async (event, featureId, subFeatureId, permissionId, roleName) => {
            const { checked } = event.target;
            const role = roleList.find(r => r.name === roleName);
            if (!role) return;

            const payload = {
                featureId,
                subFeatureId,
                roleId: role.id,
                permissionId,
                permissionHave: checked
            };

            const response = await updatePermission(payload);
            if (response?.data?.Status === 200) {
                dispatch(fetchAllEmployeePermission());
                toast.success('Permission updated successfully');
            }
        },
        [roleList, dispatch]
    );

    const mergeArrays = useCallback((array1, array2, key, keepKeysFromArray1 = []) => {
        return array2.map(item2 => {
            const item1 = array1.find(i => i[key] === item2[key]);
            if (item1) {
                keepKeysFromArray1.forEach(prop => {
                    if (Object.prototype.hasOwnProperty.call(item1, prop)) {
                        item2[prop] = item1[prop];
                    }
                });
                if (item1.SubFeatures && item2.SubFeatures) {
                    item2.SubFeatures = mergeArrays(
                        item1.SubFeatures,
                        item2.SubFeatures,
                        'subFeatureId',
                        keepKeysFromArray1
                    );
                }
            }
            return item2;
        });
    }, []);

    const handleMergePermissions = useCallback(() => {
        const merged = mergeArrays(
            _.cloneDeep(permissionList),
            _.cloneDeep(allEmployeePermission),
            'featureId',
            ['showSubPermission']
        );
        setPermissionList(merged);
    }, [permissionList, allEmployeePermission, mergeArrays]);
    console.log(roles)
    return (
        <div className="scrollableXAxis">
            <div className="permissionTbl" key={uuidv4()}>
                <div className="row mb-4">
                    <div className="col-3"></div>
                    {roles.map(role => (
                        <div key={role.name} className={`col-${role.colSize} heading alignCenter`}>
                            {role.name}
                        </div>
                    ))}
                </div>

                {permissionList.map((permission, index) => (
                    <FeatureRow
                        key={uuidv4()}
                        permission={permission}
                        index={index}
                        roles={roles}
                        onExpand={handleExpand}
                        findPermission={findPermission}
                        onPermissionChange={handlePermissionChange}
                    />
                ))}
            </div>
        </div>
    );
};

PermissionTable.propTypes = {
    dispatch: PropTypes.func.isRequired,
};

export default PermissionTable;